import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faKey, faEnvelope, faSpinner } from '@fortawesome/free-solid-svg-icons';
import SecurityService from '../../utils/securityService';
import encryptionService from '../../utils/encryptionService';
import '../../styles/components/auth/LoginForm.css';

// Determina el rol por correo cuando Firestore no responde (permiso denegado)
const getFallbackRole = (email = '') => {
    const adminList = (process.env.REACT_APP_ADMIN_EMAILS || '')
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
    return adminList.includes((email || '').toLowerCase()) ? 'admin' : 'usuario';
};

function LoginForm({ onLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [rememberMe, setRememberMe] = useState(false);

    // Verificar si hay una sesión guardada al montar el componente
    useEffect(() => {
        const checkSavedSession = async () => {
            try {
                const savedSession = encryptionService.getItem('userSession');
                if (savedSession && SecurityService.verifyToken(savedSession.token)) {
                    const userData = await getUserData(savedSession.email);
                    if (userData) {
                        onLogin(userData);
                    }
                } else {
                    // Limpiar sesión inválida
                    encryptionService.removeItem('userSession');
                }
            } catch (error) {
                console.error('Error al verificar sesión guardada:', error);
                encryptionService.removeItem('userSession');
            }
        };

        checkSavedSession();
    }, [onLogin]);

    const getUserData = async (email) => {
        try {
            const q = query(collection(db, 'users'), where('email', '==', email));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs[0]?.data();
        } catch (error) {
            console.error('Error al obtener datos del usuario:', error);
            SecurityService.logSecurityEvent('USERDATA_FETCH_ERROR', {
                email,
                error: error.message,
                code: error.code,
                timestamp: new Date().toISOString()
            });
            throw error; // Preserve original error to allow permission handling upstream
        }
    };

    const validateForm = () => {
        // Validar email
        const emailValidation = SecurityService.validateInput(email, 'email');
        if (!emailValidation.isValid) {
            setError(emailValidation.error);
            return false;
        }

        // Validar password
        const passwordValidation = SecurityService.validateInput(password, 'password');
        if (!passwordValidation.isValid) {
            setError(passwordValidation.error);
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Verificar rate limiting
        const rateLimitCheck = SecurityService.checkRateLimit('login', email);
        if (!rateLimitCheck.allowed) {
            setError(`Demasiados intentos de login. Inténtalo de nuevo en ${rateLimitCheck.retryAfter} segundos.`);
            return;
        }

        // Validar formulario
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Sanitizar inputs
            const sanitizedEmail = SecurityService.sanitizeInput(email);
            const sanitizedPassword = SecurityService.sanitizeInput(password);

            // Autenticar con Firebase
            const cred = await signInWithEmailAndPassword(auth, sanitizedEmail, sanitizedPassword);
            const firebaseUser = cred.user;

            let userData;
            try {
                userData = await getUserData(sanitizedEmail);
            } catch (userErr) {
                // Si Firestore bloquea la lectura, usar datos mínimos del usuario autenticado
                if (userErr.code === 'permission-denied') {
                    userData = {
                        id: firebaseUser.uid,
                        email: sanitizedEmail,
                        nombre: firebaseUser.displayName || sanitizedEmail,
                        rol: getFallbackRole(sanitizedEmail),
                        empresa: firebaseUser.tenantId || '',
                        assignedPdfs: []
                    };
                    SecurityService.logSecurityEvent('LOGIN_USERDATA_FALLBACK', {
                        email: sanitizedEmail,
                        timestamp: new Date().toISOString()
                    });
                } else {
                    throw userErr;
                }
            }

            if (!userData) {
                throw new Error('Usuario no encontrado en la base de datos');
            }

            // Generar token de sesión
            const sessionToken = SecurityService.generateToken(userData);

            // Guardar sesión si el usuario lo desea
            if (rememberMe) {
                const sessionData = {
                    token: sessionToken,
                    email: sanitizedEmail,
                    userId: userData.id,
                    timestamp: Date.now()
                };
                encryptionService.setItem('userSession', sessionData);
            }

            // Log de seguridad
            SecurityService.logSecurityEvent('LOGIN_SUCCESS', {
                userId: userData.id,
                email: sanitizedEmail,
                timestamp: new Date().toISOString(),
                ipAddress: 'client-side'
            });

            console.log('Usuario autenticado exitosamente');
            onLogin(userData);

        } catch (error) {
            console.error('Error en login:', error);
            
            // Log de seguridad para intento fallido
            SecurityService.logSecurityEvent('LOGIN_FAILED', {
                email: email,
                error: error.message,
                timestamp: new Date().toISOString(),
                ipAddress: 'client-side'
            });

            // Mostrar mensaje de error apropiado
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                setError('Credenciales inválidas. Verifica tu email y contraseña.');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Demasiados intentos fallidos. La cuenta ha sido temporalmente deshabilitada.');
            } else if (error.code === 'auth/invalid-email') {
                setError('El formato del email no es válido.');
            } else {
                setError('Error de autenticación. Inténtalo de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage('');

        // Verificar rate limiting para recovery
        const rateLimitCheck = SecurityService.checkRateLimit('password_reset', resetEmail);
        if (!rateLimitCheck.allowed) {
            setError(`Demasiados intentos de recuperación. Inténtalo de nuevo en ${rateLimitCheck.retryAfter} segundos.`);
            return;
        }

        // Validar email
        const emailValidation = SecurityService.validateInput(resetEmail, 'email');
        if (!emailValidation.isValid) {
            setError(emailValidation.error);
            return;
        }

        setLoading(true);

        try {
            // Sanitizar email
            const sanitizedEmail = SecurityService.sanitizeInput(resetEmail);

            // Verificar si el email existe en nuestra base de datos
            let userData = null;
            try {
                userData = await getUserData(sanitizedEmail);
            } catch (userErr) {
                if (userErr.code === 'permission-denied') {
                    // Con reglas bloqueando lectura, continuamos silenciosamente
                    userData = { email: sanitizedEmail };
                    SecurityService.logSecurityEvent('PASSWORD_RESET_USERDATA_FALLBACK', {
                        email: sanitizedEmail,
                        timestamp: new Date().toISOString(),
                        ipAddress: 'client-side'
                    });
                } else {
                    throw userErr;
                }
            }

            if (!userData) {
                // Por seguridad, no revelamos si el email existe o no
                setSuccessMessage('Si el email está registrado, recibirás instrucciones para restablecer tu contraseña.');
                setResetEmail('');
                
                // Log de intento con email no registrado
                SecurityService.logSecurityEvent('PASSWORD_RESET_INVALID_EMAIL', {
                    email: sanitizedEmail,
                    timestamp: new Date().toISOString(),
                    ipAddress: 'client-side'
                });

                setTimeout(() => {
                    setShowForgotPassword(false);
                    setSuccessMessage('');
                }, 5000);
                setLoading(false);
                return;
            }

            await sendPasswordResetEmail(auth, sanitizedEmail);
            
            // Log de recuperación exitosa
            SecurityService.logSecurityEvent('PASSWORD_RESET_SENT', {
                email: sanitizedEmail,
                userId: userData.id,
                timestamp: new Date().toISOString(),
                ipAddress: 'client-side'
            });

            setSuccessMessage('📧 Se ha enviado un email para restablecer tu contraseña. Revisa tu bandeja de entrada.');
            setResetEmail('');
            
            // Volver al login después de 5 segundos
            setTimeout(() => {
                setShowForgotPassword(false);
                setSuccessMessage('');
            }, 5000);

        } catch (error) {
            console.error('Error al enviar email de recuperación:', error);
            
            // Log de error en recuperación
            SecurityService.logSecurityEvent('PASSWORD_RESET_ERROR', {
                email: resetEmail,
                error: error.message,
                timestamp: new Date().toISOString(),
                ipAddress: 'client-side'
            });

            if (error.code === 'auth/user-not-found') {
                setSuccessMessage('Si el email está registrado, recibirás instrucciones para restablecer tu contraseña.');
            } else if (error.code === 'auth/invalid-email') {
                setError('El formato del email no es válido');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Demasiados intentos de recuperación. Inténtalo más tarde.');
            } else {
                setError('Error al enviar email de recuperación. Inténtalo de nuevo.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container" style={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(to right, #0D1421, #2BB67E)'
        }}>
            {!showForgotPassword ? (
                // FORMULARIO DE LOGIN
                <form
                    onSubmit={handleSubmit}
                    className="login-form"
                    autoComplete="off"
                    style={{
                        background: '#fff',
                        padding: '3.5rem 2.5rem',
                        borderRadius: '22px',
                        boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.18)',
                        minWidth: 380,
                        maxWidth: 440,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.7rem',
                        alignItems: 'center'
                    }}
                >
                    <img
                      src={process.env.PUBLIC_URL + '/logos/logo-login.png'}
                      alt="Logo"
                      style={{
                        height: '120px',
                        objectFit: 'contain',
                        marginBottom: '0.5rem'
                      }}
                    />

                    <div style={{
                        textAlign: 'center',
                        marginBottom: '1rem'
                    }}>
                        <h2 style={{
                            fontSize: '1.8rem',
                            fontWeight: '700',
                            color: '#2d3748',
                            marginBottom: '0.3rem'
                        }}>
                            Iniciar Sesión
                        </h2>
                        <p style={{
                            fontSize: '0.9rem',
                            color: '#718096',
                            margin: 0
                        }}>
                            Ingresa tus credenciales para acceder
                        </p>
                    </div>

                    {error && (
                        <div className="error-message" style={{
                            backgroundColor: '#fed7d7',
                            color: '#c53030',
                            padding: '0.8rem 1rem',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            border: '1px solid #feb2b2',
                            width: '100%',
                            textAlign: 'center',
                            marginBottom: '0.5rem'
                        }}>
                            {error}
                        </div>
                    )}

                    <div className="form-group" style={{
                        width: '100%',
                        position: 'relative'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#4a5568'
                        }}>
                            Email
                        </label>
                        <div style={{ position: 'relative' }}>
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                style={{
                                    position: 'absolute',
                                    left: '1rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#a0aec0',
                                    fontSize: '1rem'
                                }}
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@email.com"
                                required
                                name="login-email"
                                autoComplete="email"
                                inputMode="email"
                                spellCheck={false}
                                style={{
                                    width: '100%',
                                    padding: '1rem 1rem 1rem 2.8rem',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '12px',
                                    fontSize: '1rem',
                                    transition: 'all 0.3s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#2BB67E';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(43, 182, 126, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{
                        width: '100%',
                        position: 'relative'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#4a5568'
                        }}>
                            Contraseña
                        </label>
                        <div className="password-field-container">
                            <FontAwesomeIcon
                                icon={faKey}
                                className="password-icon"
                            />
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="password-input"
                                name="login-password"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="password-toggle-btn"
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>

                    <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.85rem'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: '#4a5568'
                        }}>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{
                                    marginRight: '0.5rem',
                                    accentColor: '#2BB67E'
                                }}
                            />
                            Recordarme
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#2BB67E',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '0.85rem'
                            }}
                        >
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '1rem',
                            backgroundColor: loading ? '#a0aec0' : '#2BB67E',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = '#22a05e';
                                e.target.style.transform = 'translateY(-1px)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = '#2BB67E';
                                e.target.style.transform = 'translateY(0)';
                            }
                        }}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                                Iniciando sesión...
                            </>
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </button>
                </form>
            ) : (
                // FORMULARIO DE RECUPERACIÓN DE CONTRASEÑA
                <form
                    onSubmit={handleForgotPassword}
                    className="login-form"
                    autoComplete="off"
                    style={{
                        background: '#fff',
                        padding: '3.5rem 2.5rem',
                        borderRadius: '22px',
                        boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.18)',
                        minWidth: 380,
                        maxWidth: 440,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.7rem',
                        alignItems: 'center'
                    }}
                >
                    <img
                      src={process.env.PUBLIC_URL + '/logos/logo-login.png'}
                      alt="Logo"
                      style={{
                        height: '80px',
                        objectFit: 'contain',
                        marginBottom: '0.5rem'
                      }}
                    />

                    <div style={{
                        textAlign: 'center',
                        marginBottom: '1rem'
                    }}>
                        <h2 style={{
                            fontSize: '1.6rem',
                            fontWeight: '700',
                            color: '#2d3748',
                            marginBottom: '0.5rem'
                        }}>
                            Recuperar Contraseña
                        </h2>
                        <p style={{
                            fontSize: '0.9rem',
                            color: '#718096',
                            margin: 0,
                            lineHeight: '1.4'
                        }}>
                            Ingresa tu email y te enviaremos instrucciones para restablecer tu contraseña
                        </p>
                    </div>

                    {error && (
                        <div className="error-message" style={{
                            backgroundColor: '#fed7d7',
                            color: '#c53030',
                            padding: '0.8rem 1rem',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            border: '1px solid #feb2b2',
                            width: '100%',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="success-message" style={{
                            backgroundColor: '#c6f6d5',
                            color: '#2f855a',
                            padding: '0.8rem 1rem',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            border: '1px solid #9ae6b4',
                            width: '100%',
                            textAlign: 'center',
                            lineHeight: '1.4'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    <div className="form-group" style={{
                        width: '100%'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#4a5568'
                        }}>
                            Email
                        </label>
                        <div style={{ position: 'relative' }}>
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                style={{
                                    position: 'absolute',
                                    left: '1rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#a0aec0',
                                    fontSize: '1rem'
                                }}
                            />
                            <input
                                type="email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                placeholder="tu@email.com"
                                required
                                name="recovery-email"
                                autoComplete="email"
                                inputMode="email"
                                spellCheck={false}
                                style={{
                                    width: '100%',
                                    padding: '1rem 1rem 1rem 2.8rem',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '12px',
                                    fontSize: '1rem',
                                    transition: 'all 0.3s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#2BB67E';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(43, 182, 126, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '1rem',
                            backgroundColor: loading ? '#a0aec0' : '#2BB67E',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                                Enviando...
                            </>
                        ) : (
                            'Enviar Email de Recuperación'
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setShowForgotPassword(false);
                            setError(null);
                            setSuccessMessage('');
                            setResetEmail('');
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#718096',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            textDecoration: 'underline'
                        }}
                    >
                        Volver al login
                    </button>
                </form>
            )}
        </div>
    );
}

export default LoginForm;